import React from 'react';
import { Info, Plus, Trash2 } from 'lucide-react';
import type { ScriptLink } from './types';

interface ExternalScriptsTabProps {
  scriptLinks: ScriptLink[];
  onAdd: () => void;
  onChange: (index: number, field: string, value: any) => void;
  onRemove: (index: number) => void;
}

export const ExternalScriptsTab: React.FC<ExternalScriptsTabProps> = ({
  scriptLinks,
  onAdd,
  onChange,
  onRemove,
}) => (
  <div className="space-y-4">
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-start space-x-2">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">External Script Links</p>
          <p>
            Load external JavaScript files from CDNs or third-party services. Perfect for libraries
            like jQuery, Bootstrap, or analytics scripts.
          </p>
        </div>
      </div>
    </div>

    <div className="space-y-3">
      {scriptLinks.map((script, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900">Script {index + 1}</h4>
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 size={16} />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Script URL *</label>
            <input
              type="url"
              value={script.src}
              onChange={e => onChange(index, 'src', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://cdn.example.com/script.js"
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={script.async || false}
                onChange={e => onChange(index, 'async', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Async</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={script.defer || false}
                onChange={e => onChange(index, 'defer', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Defer</span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID (Optional)</label>
              <input
                type="text"
                value={script.id || ''}
                onChange={e => onChange(index, 'id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="my-script"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Crossorigin (Optional)
              </label>
              <select
                value={script.crossorigin || ''}
                onChange={e => onChange(index, 'crossorigin', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">None</option>
                <option value="anonymous">anonymous</option>
                <option value="use-credentials">use-credentials</option>
              </select>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={onAdd}
        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
      >
        <Plus size={16} />
        <span>Add External Script</span>
      </button>
    </div>
  </div>
);
